/**
 * Axios instance + interceptors.
 *
 * Token storage is delegated to a small `TokenStore` interface so the
 * patient app (zustand-backed) and any other consumer can plug their
 * own storage in without this package owning a global.
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

export interface TokenStore {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  setTokens(access: string, refresh?: string): void;
  clear(): void;
}

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retriedAfterRefresh?: boolean;
  _retriedAfterStepUp?: boolean;
}

/** `detail.code` the backend sends when a binding action needs a fresh
 *  authentication (T22, `cardioq.deps.require_recent_auth`). */
export const STEP_UP_REQUIRED = 'step_up_required';

/** True for the 401 that asks for step-up — NOT an expired/invalid session,
 *  so it must never trigger the refresh-then-logout path. */
export function isStepUpRequired(err: unknown): boolean {
  const resp = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
  if (resp?.status !== 401) return false;
  const detail = (resp.data as { detail?: unknown } | null | undefined)?.detail;
  return (
    typeof detail === 'object' &&
    detail !== null &&
    (detail as { code?: unknown }).code === STEP_UP_REQUIRED
  );
}

/** Step-up endpoints answer 401 for a wrong / expired code — that is the
 *  user mistyping, not a dead session, so they skip the refresh/logout path. */
function isStepUpEndpoint(url: string | undefined): boolean {
  return !!url && url.includes('/auth/step-up/');
}

export interface ApiClientConfig {
  /**
   * Base URL for the API. Default '/api/v1' which is proxied by Vite's
   * dev server to `http://localhost:8080`. Production deploys read
   * from `import.meta.env.VITE_API_BASE` or similar at the app layer.
   */
  baseURL?: string;
  tokenStore: TokenStore;
  /**
   * Public API key sent on every `/api/v1/*` request that isn't an
   * exempted endpoint (auth/login, send-otp, etc.). Backend reads it
   * via the X-API-KEY header. Optional — apps that talk only to
   * exempted paths can omit it.
   */
  apiKey?: string;
  /**
   * Called when a 401 response indicates the token is no longer valid
   * AND the refresh-token flow has been exhausted. The app navigates
   * the user back to /login.
   */
  onUnauthenticated?: () => void;
  /**
   * Called on a 401 `step_up_required` (T22). Must prompt the user to re-prove
   * identity and resolve with the NEW access token (already stored in the
   * token store), or reject if they cancel. The original request is retried
   * once with the new token; a rejection is passed to the original caller.
   * Concurrent 401s should share one prompt — the app's implementation
   * de-dupes. Without a handler the 401 is simply rejected (never a logout).
   */
  onStepUpRequired?: () => Promise<string>;
}

let client: AxiosInstance | null = null;
let activeConfig: ApiClientConfig | null = null;
// De-dupes concurrent 401s (e.g. a poll and a user click landing together)
// into one /auth/refresh call — every waiter shares its result instead of
// each firing its own request and racing to rotate the refresh token.
let refreshPromise: Promise<string> | null = null;

async function performRefresh(config: ApiClientConfig, baseURL: string): Promise<string> {
  const refreshToken = config.tokenStore.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }
  // A bare axios call, not the configured `client` — going through the
  // intercepted instance would loop this exact 401 handler back onto
  // itself the moment a refresh token has also expired. Being bare, it
  // must add the API key itself: /auth/refresh is not API-key-exempt, so
  // without it every refresh 401'd where the key is enforced (dev) and the
  // patient was signed out at the 30-minute access-token expiry.
  const resp = await axios.post<{ access_token: string; refresh_token: string }>(
    `${baseURL}/auth/refresh`,
    { refresh_token: refreshToken },
    {
      headers: {
        'Content-Type': 'application/json',
        ...(config.apiKey ? { 'X-API-KEY': config.apiKey } : {}),
      },
    },
  );
  config.tokenStore.setTokens(resp.data.access_token, resp.data.refresh_token);
  return resp.data.access_token;
}

/**
 * Initialise the shared axios client. Call once at app startup
 * (typically inside main.tsx before rendering).
 */
export function configureApiClient(config: ApiClientConfig): AxiosInstance {
  activeConfig = config;
  const baseURL = config.baseURL ?? '/api/v1';
  client = axios.create({
    baseURL,
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use((req: InternalAxiosRequestConfig) => {
    if (!req.headers) return req;
    const token = config.tokenStore.getAccessToken();
    if (token) {
      req.headers.Authorization = `Bearer ${token}`;
    }
    if (config.apiKey) {
      req.headers['X-API-KEY'] = config.apiKey;
    }
    return req;
  });

  client.interceptors.response.use(
    (resp) => resp,
    async (err) => {
      const original = err?.config as RetryableConfig | undefined;
      // Step-up (T22) first: the session is valid, the action just needs a
      // fresh authentication. Never refresh or log out on it.
      if (isStepUpRequired(err)) {
        if (original && !original._retriedAfterStepUp && config.onStepUpRequired) {
          let newAccessToken: string;
          try {
            newAccessToken = await config.onStepUpRequired();
          } catch (cancel) {
            return Promise.reject(cancel instanceof Error ? cancel : err);
          }
          original._retriedAfterStepUp = true;
          original.headers = original.headers ?? {};
          original.headers.Authorization = `Bearer ${newAccessToken}`;
          return client!.request(original as AxiosRequestConfig);
        }
        return Promise.reject(err);
      }
      if (isStepUpEndpoint(original?.url)) {
        return Promise.reject(err);
      }
      // A 30-minute access token used to mean anyone idle on a page with
      // background polling (or just slow to click) got silently signed
      // out mid-session — the refresh token was already being stored,
      // just never spent. Now a 401 spends it once, transparently, before
      // ever bouncing the user to /login.
      if (
        err?.response?.status === 401 &&
        original &&
        !original._retriedAfterRefresh &&
        !original.url?.includes('/auth/refresh') &&
        !original.url?.includes('/auth/login')
      ) {
        try {
          refreshPromise ??= performRefresh(config, baseURL).finally(() => {
            refreshPromise = null;
          });
          const newAccessToken = await refreshPromise;
          original._retriedAfterRefresh = true;
          original.headers = original.headers ?? {};
          original.headers.Authorization = `Bearer ${newAccessToken}`;
          return client!.request(original as AxiosRequestConfig);
        } catch {
          config.tokenStore.clear();
          config.onUnauthenticated?.();
          return Promise.reject(err);
        }
      }
      if (err?.response?.status === 401) {
        config.tokenStore.clear();
        config.onUnauthenticated?.();
      }
      return Promise.reject(err);
    },
  );

  return client;
}

/**
 * Get the configured axios client. Throws if `configureApiClient` has
 * not been called — apps must wire this up at boot.
 */
export function getApiClient(): AxiosInstance {
  if (!client) {
    throw new Error(
      '@cardioq/api-client: configureApiClient() must be called before getApiClient()',
    );
  }
  return client;
}

/** For tests + sign-out — drops the current client + config. */
export function resetApiClient(): void {
  client = null;
  activeConfig = null;
}

export function getActiveTokenStore(): TokenStore {
  if (!activeConfig) {
    throw new Error('@cardioq/api-client: not configured');
  }
  return activeConfig.tokenStore;
}
